import { db } from '../config';
import { collection, addDoc, query, where, getDocs, doc, updateDoc, Query, getDoc, setDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

export interface BookingFunnelStep {
  id: string;
  customerId: string;
  sessionId: string;
  step: string;
  timestamp: Date;
  data: {
    vesselType?: string;
    startDate?: Date;
    endDate?: Date;
    timeSlot?: string;
    totalPrice?: number;
    originalPrice?: number;
    discountAmount?: number;
    promoCode?: string;
    lastStep?: number;
    errorMessage?: string;
    error?: string;
    reservationId?: string;
    
    // Enhanced date selection tracking
    dateSelectionData?: {
      datesViewed?: Date[];
      datesBrowsed?: Date[];
      datesSelected?: Date[];
      unavailableDatesViewed?: Date[];
      timeSlotsBrowsed?: string[];
      timeSlotAvailability?: {[key: string]: boolean};
      selectionDuration?: number; // Time spent selecting dates in seconds
      calendarInteractions?: number; // Number of calendar interactions
    };
    
    // Enhanced customer information
    customerInfo?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      zip?: string;
      contactPreference?: string;
      returning?: boolean;
      referralSource?: string;
      specialRequests?: string;
      marketingOptIn?: boolean;
    };
  };
  status: 'in_progress' | 'completed' | 'abandoned';
  metadata?: {
    deviceInfo?: string;
    browserInfo?: string;
    lastActivity?: Date;
    referringUrl?: string;
    entryPage?: string;
    sessionStartTime?: Date;
    sessionDuration?: number;
    screenSize?: string;
    interactionCount?: number;
  };
  steps?: {
    [key: string]: {
      timestamp: Date;
      data: any;
    };
  };
}

const COLLECTION_NAME = 'bookingFunnel';

export const trackFunnelStep = async (stepData: Partial<BookingFunnelStep>) => {
  try {
    if (!stepData.sessionId) {
      throw new Error('sessionId is required for tracking funnel steps');
    }

    // Check if a document with this sessionId already exists
    const sessionQuery = query(
      collection(db, COLLECTION_NAME),
      where('sessionId', '==', stepData.sessionId)
    );
    const sessionSnapshot = await getDocs(sessionQuery);
    
    const step: BookingFunnelStep = {
      id: stepData.id || uuidv4(),
      customerId: stepData.customerId || '',
      sessionId: stepData.sessionId,
      step: stepData.step || '',
      timestamp: stepData.timestamp || new Date(),
      data: stepData.data || {},
      status: stepData.status || 'in_progress',
      metadata: stepData.metadata || {},
      steps: {}
    };

    if (sessionSnapshot.empty) {
      // Create new session document
      step.steps = {
        [step.step]: {
          timestamp: step.timestamp,
          data: step.data
        }
      };
      await addDoc(collection(db, COLLECTION_NAME), step);
    } else {
      // Update existing session document
      const sessionDoc = sessionSnapshot.docs[0];
      const sessionData = sessionDoc.data() as BookingFunnelStep;
      
      // Update the main document data
      const updatedData = {
        ...sessionData,
        step: step.step,
        timestamp: step.timestamp,
        data: step.data,
        status: step.status,
        metadata: step.metadata,
        steps: {
          ...(sessionData.steps || {}),
          [step.step]: {
            timestamp: step.timestamp,
            data: step.data
          }
        }
      };

      await updateDoc(doc(db, COLLECTION_NAME, sessionDoc.id), updatedData);
    }

    return step;
  } catch (error) {
    console.error('Error tracking booking funnel step:', error);
    throw error;
  }
};

export const getFunnelSteps = async (options: {
  startDate?: Date;
  endDate?: Date;
  status?: BookingFunnelStep['status'];
  sessionId?: string;
}) => {
  try {
    let q: Query = collection(db, COLLECTION_NAME);
    
    if (options.sessionId) {
      q = query(q, where('sessionId', '==', options.sessionId));
    } else {
      if (options.startDate) {
        q = query(q, where('timestamp', '>=', options.startDate));
      }
      
      if (options.endDate) {
        q = query(q, where('timestamp', '<=', options.endDate));
      }

    if (options.status) {
      q = query(q, where('status', '==', options.status));
    }
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as BookingFunnelStep[];
  } catch (error) {
    console.error('Error getting booking funnel steps:', error);
    throw error;
  }
};

export const updateFunnelStepStatus = async (sessionId: string, status: BookingFunnelStep['status']) => {
  try {
    const sessionQuery = query(
      collection(db, COLLECTION_NAME),
      where('sessionId', '==', sessionId)
    );
    const sessionSnapshot = await getDocs(sessionQuery);
    
    if (!sessionSnapshot.empty) {
      const sessionDoc = sessionSnapshot.docs[0];
      await updateDoc(doc(db, COLLECTION_NAME, sessionDoc.id), { status });
    }
  } catch (error) {
    console.error('Error updating booking funnel step status:', error);
    throw error;
  }
}; 